import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Check, Plus, Bell, Mail as MailIcon, Flag, ListTodo, Users, CheckCircle2, Trash2, CalendarDays, Search, X, Sun,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Sheet, Button, Field, Input, Textarea, Select, errMsg } from '../SupportCoordination/kit';
import { LinkPicker, LINK_KINDS, fileHrefOf } from '../SupportCoordination/calendar/InviteFields';
import { avatarColor, initials, listTime, who, firstOf } from './mailUi';

// Email › Tasks: one list over the office tasks and the Support Coordination tasks (each also keeps
// its own screen), plus flagged email, the way Outlook's To Do sits beside mail. Tick to finish,
// type to add, click to change the date, reminder, person or link.
const pad = (n) => String(n).padStart(2, '0');
export const ymdLocal = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (date, n) => { const [y, m, d] = date.split('-').map(Number); return ymdLocal(new Date(y, m - 1, d + n)); };
export function groupOf(due, today) {
  if (!due) return 'none';
  const d = String(due).slice(0, 10);
  if (d < today) return 'overdue';
  if (d === today) return 'today';
  if (d === addDays(today, 1)) return 'tomorrow';
  const dow = new Date(`${today}T12:00:00`).getDay();
  if (d <= addDays(today, (7 - dow) % 7)) return 'week';
  return 'later';
}
const GROUPS = [['overdue', 'Overdue'], ['today', 'Today'], ['tomorrow', 'Tomorrow'], ['week', 'This week'], ['later', 'Later'], ['none', 'No date']];
const dueText = (due, today) => {
  const g = groupOf(due, today);
  if (g === 'today') return 'Today';
  if (g === 'tomorrow') return 'Tomorrow';
  const d = new Date(`${String(due).slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
};
const LISTS = [
  ['mine', 'My tasks', ListTodo], ['today', 'Due today', Sun], ['all', 'Everyone\'s tasks', Users], ['flagged', 'Flagged email', Flag], ['done', 'Done', CheckCircle2],
];

export function useTasks({ scope = 'mine', state = 'open' } = {}) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get('/email-tasks', { params: { scope, state } })
    .then((r) => { setRows(r.data || []); setError(null); }).catch((e) => setError(errMsg(e))), [scope, state]);
  useEffect(() => { load(); }, [load]);
  return { rows, setRows, error, load };
}
// Tick done / not done at once (back if the server says no).
export async function toggleDone(task, setRows) {
  const done = task.open;
  setRows((rs) => (rs || []).map((t) => (t.id === task.id && t.source === task.source ? { ...t, open: !done } : t)));
  try { await api.patch(`/email-tasks/${task.source}/${task.id}`, { done }); if (done) toast.success('Done', { duration: 1500 }); }
  catch (e) { toast.error(errMsg(e)); setRows((rs) => (rs || []).map((t) => (t.id === task.id && t.source === task.source ? { ...t, open: done } : t))); }
}

export default function TasksApp({ sideTop }) {
  const [list, setList] = useState('mine');
  const scope = list === 'all' ? 'all' : 'mine';
  const state = list === 'done' ? 'done' : 'open';
  const { rows, setRows, error, load } = useTasks({ scope, state });
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState('');
  const [editing, setEditing] = useState(null);
  const today = ymdLocal();
  const shown = useMemo(() => (rows || []).filter((t) => (list !== 'today' || (t.due_date && String(t.due_date).slice(0, 10) <= today))
    && (!q.trim() || [t.title, t.notes, t.link && t.link.name, t.assignee_name].filter(Boolean).join(' ').toLowerCase().includes(q.trim().toLowerCase()))), [rows, list, q, today]);
  const add = async (e) => {
    e.preventDefault();
    const title = adding.trim();
    if (!title) return;
    setAdding('');
    try {
      await api.post('/email-tasks', { title, ...(list === 'today' ? { due_date: today } : {}) });
      load();
    } catch (err) { toast.error(errMsg(err)); setAdding(title); }
  };
  return (
    <div className="relative flex h-[calc(100dvh-7rem)] min-h-[560px] bg-white rounded-2xl border border-slate-200/80 shadow-card overflow-hidden text-slate-900">
      <aside className="hidden md:flex w-[236px] flex-shrink-0 flex-col border-r border-slate-200 bg-[#fafafa]">
        <div className="p-3">{sideTop}</div>
        <nav className="px-2 pb-3 space-y-0.5" aria-label="Task lists">
          {LISTS.map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setList(k)} aria-current={list === k ? 'page' : undefined}
              className={clsx('relative w-full flex items-center gap-2.5 py-1.5 pl-2.5 pr-2 rounded-md text-[13.5px] text-left', list === k ? 'bg-sky-100/80 font-semibold text-slate-900' : 'text-slate-700 hover:bg-slate-200/50')}>
              {list === k && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#0f6cbd]" aria-hidden />}
              <Icon size={16} className={list === k ? 'text-[#0f6cbd]' : 'text-slate-500'} aria-hidden />{l}
            </button>
          ))}
        </nav>
      </aside>
      <section className="flex-1 min-w-0 flex flex-col">
        <div className="md:hidden p-3 border-b border-slate-200 space-y-2">
          {sideTop}
          <div className="flex gap-1 overflow-x-auto">{LISTS.map(([k, l]) => <button key={k} type="button" onClick={() => setList(k)} className={clsx('flex-shrink-0 px-3 h-8 rounded-full text-[12.5px] font-semibold', list === k ? 'bg-[#0f6cbd] text-white' : 'bg-slate-100 text-slate-700')}>{l}</button>)}</div>
        </div>
        <header className="flex items-center gap-3 px-5 pt-4 pb-3">
          <h2 className="text-[19px] font-semibold tracking-tight flex-1">{(LISTS.find((x) => x[0] === list) || [])[1]}</h2>
          {list !== 'flagged' && (
            <div className="relative w-48">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks" aria-label="Search tasks" className="w-full h-8 pl-8 pr-2 rounded-md bg-slate-100 text-[13px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0f6cbd]/60" />
            </div>
          )}
        </header>
        {list === 'flagged' ? <FlaggedList /> : (
          <>
            {list !== 'done' && (
              <form onSubmit={add} className="mx-5 mb-3 flex items-center gap-2 h-11 px-3 rounded-lg ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-[#0f6cbd]/60">
                <Plus size={17} className="text-[#0f6cbd]" aria-hidden />
                <input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder={list === 'today' ? 'Add a task for today' : 'Add a task'} aria-label="Add a task" className="flex-1 bg-transparent outline-none text-[14px]" />
                {adding.trim() && <button type="submit" className="text-[13px] font-semibold text-[#0f6cbd]">Add</button>}
              </form>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto mail-scroll px-5 pb-6">
              {error && <p className="text-[13px] text-rose-700 py-4">{error} <button type="button" className="font-semibold underline" onClick={load}>Try again</button></p>}
              {rows === null && !error && <p className="text-[13px] text-slate-500 py-6">Loading…</p>}
              {rows && !shown.length && <EmptyTasks list={list} />}
              {rows && shown.length > 0 && (list === 'done'
                ? <TaskRows tasks={shown} today={today} onToggle={(t) => toggleDone(t, setRows)} onOpen={setEditing} />
                : GROUPS.map(([g, label]) => {
                  const ts = shown.filter((t) => groupOf(t.due_date, today) === g);
                  if (!ts.length) return null;
                  return (
                    <div key={g} className="mt-3">
                      <p className={clsx('text-[12px] font-semibold uppercase tracking-wider mb-1', g === 'overdue' ? 'text-rose-600' : 'text-slate-500')}>{label} <span className="text-slate-400 font-medium">{ts.length}</span></p>
                      <TaskRows tasks={ts} today={today} onToggle={(t) => toggleDone(t, setRows)} onOpen={setEditing} showAssignee={list === 'all'} />
                    </div>
                  );
                }))}
            </div>
          </>
        )}
      </section>
      <TaskSheet task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function EmptyTasks({ list }) {
  const text = { mine: 'Nothing to do. Add a task above, or make one from an email.', today: 'Nothing due today.', all: 'No open tasks in the company.', done: 'Finished tasks show here.' }[list];
  return <div className="py-14 text-center"><CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" aria-hidden /><p className="text-[13.5px] text-slate-600">{text}</p></div>;
}

export function TaskRows({ tasks, today, onToggle, onOpen, showAssignee, compact }) {
  const navigate = useNavigate();
  return (
    <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100 bg-white">
      {tasks.map((t) => {
        const K = t.link && LINK_KINDS[t.link.module];
        const late = t.open && t.due_date && groupOf(t.due_date, today) === 'overdue';
        return (
          <li key={`${t.source}:${t.id}`} className={clsx('group flex items-start gap-3', compact ? 'px-2.5 py-2' : 'px-3 py-2.5')}>
            <button type="button" onClick={() => onToggle(t)} role="checkbox" aria-checked={!t.open} aria-label={t.open ? `Mark "${t.title}" done` : `Mark "${t.title}" not done`}
              className={clsx('mt-0.5 w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ring-2 transition-colors', t.open ? 'ring-slate-300 hover:ring-[#0f6cbd] hover:bg-sky-50 text-transparent hover:text-[#0f6cbd]' : 'bg-[#0f6cbd] ring-[#0f6cbd] text-white')}>
              <Check size={12} strokeWidth={3} aria-hidden />
            </button>
            <button type="button" onClick={() => onOpen && onOpen(t)} className="flex-1 min-w-0 text-left">
              <span className={clsx('block text-[13.5px] break-words', t.open ? 'text-slate-900' : 'text-slate-400 line-through')}>{t.title}</span>
              <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-0.5 text-[11.5px] text-slate-500">
                {t.due_date && <span className={clsx('inline-flex items-center gap-1', late && 'text-rose-600 font-semibold')}><CalendarDays size={11} aria-hidden />{dueText(t.due_date, today)}</span>}
                {t.reminder_at && t.open && <span className="inline-flex items-center gap-1"><Bell size={11} aria-hidden />{new Date(t.reminder_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>}
                {K && <span className={clsx('px-1.5 py-px rounded font-semibold ring-1', K.chip)}>{K.short} · {t.link.name}</span>}
                {t.source === 'sc' && !K && <span className="px-1.5 py-px rounded font-semibold ring-1 bg-violet-50 text-violet-700 ring-violet-200">SC</span>}
                {showAssignee && t.assignee_name && <span>{t.assignee_name}</span>}
              </span>
            </button>
            {t.source_message_id && (
              <button type="button" onClick={() => navigate(`/mail?open=${t.source_message_id}`)} title="Open the email it came from" aria-label="Open the email it came from"
                className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-[#0f6cbd] hover:bg-slate-100"><MailIcon size={14} aria-hidden /></button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// Flagged email, Outlook's other kind of to-do.
function FlaggedList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get('/mail/messages', { params: { view: 'flagged', limit: 100 } }).then((r) => setRows((r.data && r.data.rows) || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const unflag = async (m) => {
    setRows((rs) => rs.filter((x) => x.id !== m.id));
    try { await api.post('/mail/messages/bulk', { ids: [m.id], action: 'unflag' }); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  if (rows === null) return <p className="px-5 text-[13px] text-slate-500">Loading…</p>;
  if (!rows.length) return <div className="py-14 text-center"><Flag size={26} className="mx-auto text-slate-300 mb-2" aria-hidden /><p className="text-[13.5px] text-slate-600">No flagged email. Flag a message to keep it here until it is dealt with.</p></div>;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto mail-scroll px-5 pb-6">
      <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
        {rows.map((m) => {
          const p = firstOf(m.from_addr) || {};
          return (
            <li key={m.id} className="flex items-center gap-3 px-3 py-2.5">
              <button type="button" onClick={() => unflag(m)} role="checkbox" aria-checked={false} aria-label="Done: remove the flag" title="Done: remove the flag"
                className="w-5 h-5 rounded-full ring-2 ring-slate-300 hover:ring-[#0f6cbd] text-transparent hover:text-[#0f6cbd] flex items-center justify-center flex-shrink-0"><Check size={12} strokeWidth={3} aria-hidden /></button>
              <span className="w-8 h-8 rounded-full text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: avatarColor(p.address) }} aria-hidden>{initials(p.name, p.address)}</span>
              <button type="button" onClick={() => navigate(`/mail?open=${m.id}`)} className="flex-1 min-w-0 text-left">
                <span className="block text-[13.5px] text-slate-900 truncate">{m.subject || '(no subject)'}</span>
                <span className="block text-[11.5px] text-slate-500 truncate">{who(p)} · {listTime(m.msg_date)}</span>
              </button>
              <Flag size={14} className="text-rose-600 fill-rose-600 flex-shrink-0" aria-hidden />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Change a task: name, notes, due date, reminder, who it is for, what it is linked to.
const toLocalInput = (iso) => { if (!iso) return ''; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
export function TaskSheet({ task, onClose, onSaved }) {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!task) { setF(null); return; }
    setF({ title: task.title || '', notes: task.notes || '', due_date: task.due_date ? String(task.due_date).slice(0, 10) : '', reminder: toLocalInput(task.reminder_at), assignee_id: task.assignee_id || '', link: task.link || null });
    api.get('/email-tasks/people').then((r) => setPeople(r.data || [])).catch(() => {});
  }, [task]);
  if (!task || !f) return null;
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { title: f.title, notes: f.notes, due_date: f.due_date || null, reminder_at: f.reminder ? new Date(f.reminder).toISOString() : null, assignee_id: f.assignee_id || null };
      if (JSON.stringify(f.link || null) !== JSON.stringify(task.link || null)) body.link = f.link;
      await api.patch(`/email-tasks/${task.source}/${task.id}`, body);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try { await api.delete(`/email-tasks/${task.source}/${task.id}`); toast.success('Deleted'); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const fileHref = task.link ? fileHrefOf(task.link) : null;
  return (
    <Sheet open={!!task} onClose={onClose} title="Task" icon={ListTodo} hue="indigo"
      sub={task.source === 'sc' ? 'A Support Coordination task: it is also on the participant\'s SC file.' : 'An office task: it is also on the Tasks screen.'}
      footer={<><Button variant="ghost" className="mr-auto text-rose-600 hover:bg-rose-50" icon={Trash2} onClick={remove}>Delete</Button><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={save}>Save</Button></>}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <Field label="Task"><Input value={f.title} onChange={(e) => set({ title: e.target.value })} autoFocus /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Due"><Input type="date" value={f.due_date} onChange={(e) => set({ due_date: e.target.value })} /></Field>
          <Field label="Remind me" hint="An email and a notice at this time."><Input type="datetime-local" value={f.reminder} onChange={(e) => set({ reminder: e.target.value })} /></Field>
        </div>
        <Field label="For"><Select value={f.assignee_id} allowEmpty emptyLabel="No one" onChange={(v) => set({ assignee_id: v })}
          options={people.map((p) => ({ value: p.id, label: `${p.first_name || ''} ${p.last_name || ''}`.trim() + (String(p.id) === String(user && user.id) ? ' (me)' : '') }))} /></Field>
        <Field label="Linked to" hint={task.source === 'sc' ? 'An SC task stays with an SC participant.' : 'A PM participant or a client.'}><LinkPicker value={f.link} onChange={(link) => set({ link })} /></Field>
        <Field label="Notes"><Textarea rows={4} value={f.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
        <div className="flex flex-wrap gap-2">
          {task.source_message_id && <Button size="sm" variant="secondary" icon={MailIcon} onClick={() => navigate(`/mail?open=${task.source_message_id}`)}>Open the email</Button>}
          {fileHref && <Button size="sm" variant="secondary" onClick={() => navigate(fileHref)}>Open {task.link.name}'s file</Button>}
          {task.open === false && <Button size="sm" variant="ghost" icon={X} onClick={async () => { await api.patch(`/email-tasks/${task.source}/${task.id}`, { done: false }); onSaved(); }}>Not done after all</Button>}
        </div>
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Sheet>
  );
}
